/**
 * PersonaSelector Component
 *
 * Allows users to select between Mark Ellison and Sarah Lawson personas
 * for transformation diagnostics. Each persona represents a different
 * pattern of organizational strain.
 */

import React from 'react';
import { PERSONA_BASELINES } from '../utils/algorithms';

function PersonaSelector({ selectedPersona, onChange, diagnosticType }) {
  const personas = [
    {
      id: 'mark',
      ...PERSONA_BASELINES.mark,
      avatar: '👨‍💼',
      color: 'blue',
      variesIn: diagnosticType === 'system_burden' ? 'Organizational Dependencies' : 'Leadership Anxieties'
    },
    {
      id: 'sarah',
      ...PERSONA_BASELINES.sarah,
      avatar: '👩‍💼',
      color: 'purple',
      variesIn: diagnosticType === 'system_burden' ? 'Organizational Dependencies' : 'Leadership Anxieties'
    }
  ];

  return (
    <div className="persona-selector mb-8">
      <h2 className="text-2xl font-bold mb-4">Choose Your Profile</h2>
      <p className="text-gray-600 mb-6">
        Select the profile that best matches your current transformation challenge.
        This helps us provide more accurate insights.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {personas.map((persona) => (
          <button
            key={persona.id}
            onClick={() => onChange(persona.id)}
            className={`
              p-6 rounded-lg border-2 text-left transition-all
              ${selectedPersona === persona.id
                ? `border-${persona.color}-600 bg-${persona.color}-50 shadow-lg`
                : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
              }
            `}
          >
            <div className="flex items-start mb-4">
              <span className="text-4xl mr-4">{persona.avatar}</span>
              <div>
                <h3 className="text-xl font-bold">
                  {persona.id === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'}
                </h3>
                <p className="text-sm text-gray-600">{persona.role}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center mb-2">
                <span className="text-lg font-semibold text-red-600">
                  {persona.strain}
                </span>
              </div>
              <p className="text-sm text-gray-700">{persona.description}</p>
            </div>

            <div className="bg-gray-100 p-3 rounded text-sm">
              <p className="font-semibold mb-1">In this diagnostic:</p>
              <p className="text-gray-700">
                We'll assess your <strong>{persona.variesIn}</strong>
                {diagnosticType === 'system_burden' && (
                  <span className="block mt-1 text-xs text-gray-600">
                    (Baseline leadership flexibility: {(persona.lpff * 100).toFixed(0)}%)
                  </span>
                )}
                {diagnosticType === 'leadership_burden' && (
                  <span className="block mt-1 text-xs text-gray-600">
                    (Baseline organizational capability: {(persona.dcf * 100).toFixed(0)}%)
                  </span>
                )}
              </p>
            </div>

            {selectedPersona === persona.id && (
              <div className="mt-4 flex items-center text-green-600">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Selected
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">About these profiles</p>
            <p>
              These profiles represent common patterns we see in transformation leaders.
              Your selection helps us provide more personalized insights based on research-backed baselines.
              {diagnosticType === 'system_burden' && ' We\'ll measure how your organizational dependencies compare to this profile\'s typical leadership flexibility.'}
              {diagnosticType === 'leadership_burden' && ' We\'ll measure how your leadership anxieties compare to this profile\'s typical organizational capability.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PersonaSelector;
